"use client";

import { signin } from "@/app/(auth)/signin/actions";
import { SigninFormData, signinSchema } from "@/app/(auth)/signin/schema";
import { ProviderAuthButton } from "@/components/auth/provider-auth-button";
import PasswordInput from "@/components/password-input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { AppRoutes, NavigationConfig } from "@/config/navigation-config";
import { handleFormErrors } from "@/lib/form-errors";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconLock, IconMail } from "@tabler/icons-react";
import Link from "next/link";
import { useTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Button, buttonVariants } from "../../../components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";

type SigninFormProps = {
  afterSignin?: () => void;
};

function SigninForm({ afterSignin }: SigninFormProps) {
  const [isPending, startTransition] = useTransition();

  const form = useForm<SigninFormData>({
    resolver: zodResolver(signinSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleLogin = (data: SigninFormData) => {
    startTransition(async () => {
      const { errors } = await signin(data);

      if (errors) {
        handleFormErrors(form.setError, errors);
        return;
      }

      toast.success("Signed in successfully");
      form.reset();
      afterSignin?.();
    });
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleLogin)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput
                      {...field}
                      type="email"
                      disabled={isPending}
                      placeholder="Enter your email"
                    />
                    <InputGroupAddon>
                      <IconMail className="h-4 w-4 text-zinc-400" />
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput
                    {...field}
                    prefix={<IconLock className="h-4 w-4 text-zinc-400" />}
                    disabled={isPending}
                    autoComplete="password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Link
            href={NavigationConfig.getPath(AppRoutes.FORGOT_PASSWORD)}
            className={cn(
              buttonVariants({ variant: "link" }),
              "text-muted-foreground px-0",
            )}
          >
            Forgot Password?
          </Link>
          <Button
            type="submit"
            className={cn(
              "h-11 w-full font-medium text-white",
              "bg-linear-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            disabled={isPending}
          >
            {isPending ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </Form>

      <ProviderAuthButton disabled={isPending} />
    </div>
  );
}

export default SigninForm;
